import { useState } from "react";
import { createRoute, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Ticket, TriangleAlert } from "lucide-react";

import { rootRoute } from "./root.tsx";
import { SubPage } from "../components/layout.tsx";
import { Button, Empty, ErrorBlock, Input, Price, Spinner } from "../components/ui.tsx";
import { useIdentities, useMe } from "../api/queries.ts";
import { orgFetch } from "../api/org-client.ts";
import { useIsDesktop } from "../lib/viewport.ts";
import { DesktopCoupons } from "./desktop/coupons.tsx";

/**
 * 我的优惠券 —— 商家/平台发券。
 *
 * 🔴 **谁发的券谁掏钱。** 券让买家少付的部分会记成发券方名下的一笔负分账，
 *    所以这一页必须把「已经花了多少」摆在最显眼的位置 ——
 *    发券方看不到成本的话，很容易发出一张比自己差价还大的券，卖一单亏一单。
 */

export interface Coupon {
  id: number;
  code: string;
  name: string;
  kind: "fixed" | "percent";
  value: number;
  threshold: number;
  totalCount: number;
  usedCount: number;
  perUserLimit: number;
  active: boolean;
  endAt: string | null;
  /** 这张券到目前为止花了我多少钱。 */
  spent: number;
}

function CouponRow({ c, orgId }: { c: Coupon; orgId: number }) {
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: () =>
      orgFetch(`/coupons/${c.id}/toggle`, orgId, {
        method: "POST",
        body: JSON.stringify({ active: !c.active }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["coupons", orgId] }),
  });

  const face =
    c.kind === "fixed" ? (
      <>
        减 <Price cents={c.value} />
      </>
    ) : (
      <>打 {(100 - c.value) / 10} 折</>
    );

  return (
    <div className="border-b border-neutral-100 px-4 py-3 last:border-0">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm">
            {c.name}
            {!c.active && (
              <span className="ml-1.5 rounded bg-neutral-100 px-1.5 py-0.5 text-[11px] text-ink-500">
                已停用
              </span>
            )}
          </p>
          <p className="mt-1 font-mono text-xs text-brand-600">{c.code}</p>
          <p className="mt-1 text-xs text-ink-500">
            {face}
            {c.threshold > 0 && (
              <>
                {" · "}满 <Price cents={c.threshold} /> 可用
              </>
            )}
            {" · "}
            已用 {c.usedCount}
            {c.totalCount > 0 ? ` / ${c.totalCount}` : " 张（不限量）"}
          </p>
          {/* 🔴 成本摆在明面上：发券方掏的是自己的差价 */}
          <p className="mt-1 text-xs">
            <span className="text-ink-500">这张券花了我</span>{" "}
            <Price cents={c.spent} className="text-red-600" />
          </p>
        </div>
        <button
          className="shrink-0 text-xs text-ink-500"
          onClick={() => toggle.mutate()}
          disabled={toggle.isPending}
        >
          {c.active ? "停用" : "启用"}
        </button>
      </div>
      {toggle.error && <ErrorBlock error={toggle.error} />}
    </div>
  );
}

function Coupons() {
  const search = useSearch({ from: "/coupons" }) as { orgId?: number };
  const { data: me, isPending: mePending } = useMe();
  const { data: identities } = useIdentities(Boolean(me));
  const qc = useQueryClient();

  /** 能发券的是链上的转售方：平台、经销商、分销商 —— 跟定价是同一批。 */
  const orgs = (identities?.orgs ?? []).filter(
    (o) =>
      o.status === "active" &&
      (o.type === "platform" || o.type === "dealer" || o.type === "distributor"),
  );
  const [picked, setPicked] = useState<number | null>(search.orgId ?? null);
  const orgId = picked ?? orgs[0]?.id ?? null;

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    code: "",
    name: "",
    kind: "fixed" as "fixed" | "percent",
    value: "",
    threshold: "",
    totalCount: "",
  });

  const list = useQuery({
    queryKey: ["coupons", orgId],
    queryFn: () => orgFetch<Coupon[]>("/coupons", orgId!),
    enabled: Boolean(orgId),
  });

  const create = useMutation({
    mutationFn: () =>
      orgFetch("/coupons", orgId!, {
        method: "POST",
        body: JSON.stringify({
          code: form.code.toUpperCase(),
          name: form.name,
          kind: form.kind,
          /** ⚠️ 满减填的是元、打折填的是折扣数，两条路不能混着换算。 */
          value: form.kind === "fixed" ? Math.round(Number(form.value) * 100) : Number(form.value),
          threshold: form.threshold ? Math.round(Number(form.threshold) * 100) : 0,
          totalCount: form.totalCount ? Number(form.totalCount) : 0,
        }),
      }),
    onSuccess: () => {
      setOpen(false);
      setForm({ code: "", name: "", kind: "fixed", value: "", threshold: "", totalCount: "" });
      void qc.invalidateQueries({ queryKey: ["coupons", orgId] });
    },
  });

  if (mePending) {
    return (
      <SubPage title="优惠券">
        <Spinner />
      </SubPage>
    );
  }

  if (!me || orgs.length === 0) {
    return (
      <SubPage title="优惠券">
        <Empty title="你名下没有可发券的店" hint="发券是商家功能，联系上级商家或平台开通" />
      </SubPage>
    );
  }

  return (
    <SubPage title="优惠券">
      {orgs.length > 1 && (
        <div className="flex gap-2 overflow-x-auto bg-white px-4 py-2.5">
          {orgs.map((o) => (
            <button
              key={o.id}
              onClick={() => setPicked(o.id)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-sm ${
                o.id === orgId ? "bg-brand-600 text-white" : "bg-neutral-100 text-ink-500"
              }`}
            >
              {o.name}
            </button>
          ))}
        </div>
      )}

      {/* 🔴 这条提示不是客套话：券比差价大就是卖一单亏一单 */}
      <div className="bg-amber-50 px-4 py-2.5 text-xs text-amber-800">
        <p className="flex items-center gap-1.5">
          <TriangleAlert className="size-3.5" />
          券的钱从<strong>你自己的差价</strong>里出
        </p>
        <p className="mt-0.5">
          面额比你在那件商品上赚的还多的话，这一单你是净亏的。发之前对一下「我的定价」。
        </p>
      </div>

      <div className="mt-2 bg-white">
        {open ? (
          <div className="space-y-3 px-4 py-4">
            <Input
              label="券码"
              placeholder="如 SPRING-50，客户下单时输入"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            />
            <Input
              label="名称"
              placeholder="如 春季装修节满 500 减 50"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <div className="flex gap-2">
              {(["fixed", "percent"] as const).map((k) => (
                <button
                  key={k}
                  onClick={() => setForm({ ...form, kind: k, value: "" })}
                  className={`flex-1 rounded-lg py-2 text-sm ${
                    form.kind === k ? "bg-brand-600 text-white" : "bg-neutral-100 text-ink-500"
                  }`}
                >
                  {k === "fixed" ? "满减" : "打折"}
                </button>
              ))}
            </div>
            <Input
              label={form.kind === "fixed" ? "减多少（元）" : "折扣（填 5 表示 95 折）"}
              inputMode="decimal"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
            />
            <Input
              label="使用门槛（元，不填 = 无门槛）"
              inputMode="decimal"
              value={form.threshold}
              onChange={(e) => setForm({ ...form, threshold: e.target.value })}
            />
            <Input
              label="发行量（不填 = 不限量）"
              inputMode="numeric"
              value={form.totalCount}
              onChange={(e) => setForm({ ...form, totalCount: e.target.value })}
            />
            {create.error && <ErrorBlock error={create.error} />}
            <div className="flex gap-2">
              <Button
                className="flex-1"
                loading={create.isPending}
                disabled={!form.code || !form.name || !form.value}
                onClick={() => create.mutate()}
              >
                发这张券
              </Button>
              <Button variant="ghost" onClick={() => setOpen(false)}>
                取消
              </Button>
            </div>
          </div>
        ) : (
          <button
            className="flex w-full items-center justify-between px-4 py-3.5"
            onClick={() => setOpen(true)}
          >
            <span className="flex items-center gap-2 text-sm">
              <Ticket className="size-4 text-ink-500" />
              发一张新券
            </span>
            <span className="text-xs text-brand-600">去发券 →</span>
          </button>
        )}
      </div>

      {list.isPending ? (
        <Spinner />
      ) : list.error ? (
        <ErrorBlock error={list.error} onRetry={() => list.refetch()} />
      ) : list.data.length === 0 ? (
        <Empty title="还没发过券" hint="发一张券，客户结算时输券码就能用" />
      ) : (
        <div className="mt-2 bg-white">
          {list.data.map((c) => (
            <CouponRow key={c.id} c={c} orgId={orgId!} />
          ))}
        </div>
      )}
    </SubPage>
  );
}

/** 手机版 / 网页版是两套页面，按视口挑一套（URL 与数据 hooks 两边共用）。 */
function CouponsRoute() {
  return useIsDesktop() ? <DesktopCoupons /> : <Coupons />;
}

export const couponsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/coupons",
  component: CouponsRoute,
  validateSearch: (s: Record<string, unknown>): { orgId?: number } => ({
    orgId: s.orgId ? Number(s.orgId) : undefined,
  }),
});
